import { CommonModule, DatePipe, NgComponentOutlet } from '@angular/common';
import { Component, computed, inject, input, Type } from '@angular/core';
import { TaskStatus } from '@generated/models';
import { SearchActivityEntry } from '@web/shared/components/search/interfaces/search-activity-entry.interface';
import { contactLink } from '@web/shared/pipes/contact-link.pipe';
import { constructionSiteLink } from '@web/shared/pipes/construction-site-link.pipe';
import { projectLink } from '@web/shared/pipes/project-link.pipe';
import { realEstateLink } from '@web/shared/pipes/real-estate-link.pipe';
import { bidLink } from '@web/shared/pipes/bid-link.pipe';
import { isEmpty } from 'class-validator';
import { TagModule } from 'primeng/tag';
import { BidTagComponent } from '../../bid-tag.component';
import { ContactCodeTagComponent } from '../../contact-code-tag.component';
import { ConstructionSiteCodeTagComponent } from '../../construction-site-code-tag.component';
import { EmployeeCodeTagComponent } from '../../employee-code-tag.component';
import { LeadCodeTagComponent } from '../../lead-code-tag.component';
import { OwnerCompanyCodeTagComponent } from '../../owner-company-code-tag.component';
import { ProjectCodeTagComponent } from '../../project-code-tag.component';
import { RealEstateCodeTagComponent } from '../../real-estate-code-tag.component';
import { ScoutingCodeTagComponent } from '../../scouting-code-tag.component';
import { SupplierCodeTagComponent } from '../../supplier-code-tag.component';
import { DetailItem, SearchItemBaseComponent } from './search-item-base.component';

@Component({
  selector: 'app-search-document-asset-item',
  imports: [CommonModule, TagModule, NgComponentOutlet, SearchItemBaseComponent],
  providers: [DatePipe],
  template: `
    @let _item = item();

    <app-search-item-base
      icon="pi pi-document"
      [detailRouterLink]="link()"
      [color]="iconColor[_item.relatedTo.aggregate]"
      [detailItems]="detailItems()"
    >
      <ng-template #title>
        <div [innerHTML]="_item.name"></div>
      </ng-template>

      <ng-template #codeTag>
        @let tagComponents = codeTagComponents[_item.relatedTo.aggregate];
        <ng-container *ngComponentOutlet="tagComponents[0]; inputs: tagComponents[1](_item)"> </ng-container>
      </ng-template>
    </app-search-item-base>
  `,
})
export class SearchDocumentAssetItemComponent {
  readonly item = input.required<SearchActivityEntry>();

  readonly datePipe = inject(DatePipe);

  readonly detailItems = computed<DetailItem[]>(() => {
    const item = this.item();

    return [
      {
        icon: 'pi pi-pen-to-square',
        content: item.description,
        isHtml: true,
      },
      {
        icon: 'pi pi-calendar',
        content: this.datePipe.transform(item.scheduledAt) || '',
        isHtml: true,
      },
      {
        icon: 'pi pi-circle-fill',
        content: this.taskStatus[item.status],
        isHtml: false,
      },
      {
        icon: 'pi pi-clock',
        content: this.datePipe.transform(item.createdAt) || '',
        isHtml: false,
      },
    ].filter((v) => !isEmpty(v.content));
  });

  readonly link = computed(() => {
    const item = this.item();
    const links: Record<SearchActivityEntry['relatedTo']['aggregate'], () => string[]> = {
      Contact: () =>
        contactLink({ id: item.relatedTo.id, kind: item.relatedTo.kind as 'naturalPerson' | 'legalPerson' }, ['tasks']),
      RealEstate: () =>
        realEstateLink({ id: item.relatedTo.id, kind: item.relatedTo.kind as 'unit' | 'group' }, ['tasks']),
      Lead: () => ['/', 'leads', item.relatedTo.id, 'tasks'],
      Scouting: () => ['/', 'scoutings', item.relatedTo.id, 'tasks'],
      Supplier: () => ['/', 'suppliers', item.relatedTo.id, 'tasks'],
      Employee: () => ['/', 'employees', item.relatedTo.id, 'tasks'],
      OwnerCompany: () => ['/', 'owner-company'],
      Project: () => projectLink({ id: item.relatedTo.id }, ['tasks']),
      ConstructionSite: () => constructionSiteLink({ id: item.relatedTo.id }, ['tasks']),
      Bid: () => bidLink({ id: item.relatedTo.id }, ['tasks']),
    };

    return links[item.relatedTo.aggregate]();
  });

  readonly codeTagComponents: Record<
    SearchActivityEntry['relatedTo']['aggregate'],
    [Type<unknown>, (item: SearchActivityEntry) => Record<string, unknown>]
  > = {
    Contact: [ContactCodeTagComponent, (item: SearchActivityEntry) => ({ contact: item.relatedTo, clickable: false })],
    Lead: [LeadCodeTagComponent, (item: SearchActivityEntry) => ({ lead: item.relatedTo, clickable: false })],
    RealEstate: [
      RealEstateCodeTagComponent,
      (item: SearchActivityEntry) => ({ realEstate: item.relatedTo, clickable: false }),
    ],
    Scouting: [
      ScoutingCodeTagComponent,
      (item: SearchActivityEntry) => ({ scouting: item.relatedTo, clickable: false }),
    ],
    Supplier: [
      SupplierCodeTagComponent,
      (item: SearchActivityEntry) => ({ supplier: item.relatedTo, clickable: false }),
    ],
    Employee: [
      EmployeeCodeTagComponent,
      (item: SearchActivityEntry) => ({ employee: item.relatedTo, clickable: false }),
    ],
    OwnerCompany: [OwnerCompanyCodeTagComponent, () => ({ clickable: false })],
    Project: [ProjectCodeTagComponent, (item: SearchActivityEntry) => ({ project: item.relatedTo, clickable: false })],
    ConstructionSite: [
      ConstructionSiteCodeTagComponent,
      (item: SearchActivityEntry) => ({ constructionSite: item.relatedTo, clickable: false }),
    ],
    Bid: [BidTagComponent, (item: SearchActivityEntry) => ({ bid: item.relatedTo, clickable: false })],
  };

  readonly iconColor: Record<SearchActivityEntry['relatedTo']['aggregate'], string> = {
    Contact: 'text-soe-twilight-blue',
    Lead: 'text-soe-tropical-green',
    RealEstate: 'text-soe-orange-glow',
    Scouting: 'text-soe-golden-sand',
    Supplier: 'text-soe-ocean-blue',
    Employee: 'text-soe-orchid-purple',
    OwnerCompany: 'text-soe-ocean-blue',
    Project: 'text-soe-sage-mist',
    ConstructionSite: 'text-soe-brick-clay',
    Bid: 'text-soe-petrol-blue',
  };

  readonly taskStatus: Record<string, string> = {
    [TaskStatus.ToDo]: $localize`To Do`,
    [TaskStatus.InProgress]: $localize`In Progress`,
    [TaskStatus.Completed]: $localize`Completed`,
    [TaskStatus.Failed]: $localize`Failed`,
  };
}
